<template>
    <v-form ref="form" v-model="valid" class="person-form" data-cy="person-form">
        <v-row class="company-profile__header" dense>
            <v-col cols="12" sm="6" md="2">
                <LocaleSwitcher v-if="isCorporateUser" @change="changeActiveLangCode" />
            </v-col>
            <v-col cols="12" sm="6" md="10" class="align-center">
                <v-row v-if="person.personId" dense>
                    <p v-if="person.personId" class="text-subtitle-2 text-grey font-weight-regular ml-auto px-4">
                        {{ `${$t('id')}: ${person.personId}` }}
                    </p>
                </v-row>
                <v-row v-if="person.personId" dense class="px-4 pb-3">
                    <v-switch
                        v-model="person.active"
                        color="primary"
                        class="ml-auto"
                        density="compact"
                        hide-details
                        :label="person.active ? $t('active') : $t('inactive')"
                        data-cy="active"
                    />
                </v-row>
            </v-col>
        </v-row>
        <v-row class="person-form__body">
            <v-col class="person-form__body-main" cols="12" md="9">
                <v-row>
                    <v-col class="col-title" cols="12" md="3">
                        <h4 class="person-form__title text-h6 text--secondary">
                            {{ $t('personal_information') }}
                        </h4>
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row dense>
                            <v-col cols="12" :sm="isCorporateUser ? 6 : 4" :md="isCorporateUser ? 3 : 4">
                                <v-select
                                    v-model="person.prefix"
                                    color="primary"
                                    variant="outlined"
                                    density="compact"
                                    data-cy="prefix"
                                    clearable
                                    :disabled="dontAllowEdit"
                                    :items="['Mr', 'Ms', 'Mrs']"
                                    :label="$t('salutation')"
                                />
                            </v-col>
                            <v-col v-if="isCorporateUser" cols="12" sm="6" md="3">
                                <TextFieldLocale
                                    v-model="person.firstNameLocalized[activeLangCode]"
                                    :label="`${$t('first_name')}*`"
                                    :active-lang-code="activeLangCode"
                                    data-cy="first-name-localized"
                                    :rules="[requiredRule('first_name')]"
                                />
                            </v-col>
                            <v-col v-if="isCorporateUser" cols="12" sm="6" md="3">
                                <TextFieldLocale
                                    v-model="person.middleNameLocalized[activeLangCode]"
                                    :label="$t('middle_name')"
                                    :active-lang-code="activeLangCode"
                                    data-cy="middle-name-localized"
                                />
                            </v-col>
                            <v-col v-if="isCorporateUser" cols="12" sm="6" md="3">
                                <TextFieldLocale
                                    v-model="person.lastNameLocalized[activeLangCode]"
                                    :label="`${$t('last_name')}*`"
                                    :active-lang-code="activeLangCode"
                                    data-cy="last-name-localized"
                                    :rules="[requiredRule('last_name')]"
                                />
                            </v-col>
                            <v-col v-if="!isCorporateUser" cols="12" sm="4">
                                <v-text-field
                                    v-model="person.firstName"
                                    color="primary"
                                    variant="outlined"
                                    density="compact"
                                    data-cy="first-name"
                                    :disabled="dontAllowEdit"
                                    :label="$t('first_name')"
                                />
                            </v-col>
                            <v-col v-if="!isCorporateUser" cols="12" sm="4">
                                <v-text-field
                                    v-model="person.lastName"
                                    color="primary"
                                    variant="outlined"
                                    density="compact"
                                    data-cy="last-name"
                                    :disabled="dontAllowEdit"
                                    :label="`${$t('last_name')}*`"
                                    :rules="[requiredRule('last_name')]"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <phone-field
                                    v-model="person.contactPhone"
                                    color="primary"
                                    variant="outlined"
                                    density="compact"
                                    data-cy="contact-phone"
                                    :disabled="dontAllowEdit"
                                    :label="$t('phone')"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="person.email"
                                    color="primary"
                                    variant="outlined"
                                    density="compact"
                                    data-cy="email"
                                    type="email"
                                    :disabled="dontAllowEdit"
                                    :label="isSupplierCompany ? `${$t('email')}*` : $t('email')"
                                    :rules="isSupplierCompany ? [requiredRule('email'), emailRule('email')] : [emailRule('email')]"
                                />
                            </v-col>
                            <v-col v-show="!hideSkype" cols="12" sm="4">
                                <v-text-field
                                    v-model="person.skype"
                                    color="primary"
                                    variant="outlined"
                                    density="compact"
                                    data-cy="skype"
                                    :disabled="dontAllowEdit"
                                    :label="`${$t('skype')}`"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="person.icq"
                                    color="primary"
                                    variant="outlined"
                                    density="compact"
                                    data-cy="messenger"
                                    :disabled="dontAllowEdit"
                                    :label="`${$t('messenger')}`"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row v-show="!hideBirthdate || !hideCitizenship">
                    <v-col class="col-title" cols="12" md="3">
                        <h4 class="person-form__title text-h6 text--secondary">
                            {{ $t('additional_information') }}
                        </h4>
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row v-show="!hideBirthdate || !hideCitizenship" dense>
                            <v-col v-show="!hideBirthdate" cols="12" sm="4">
                                <date-picker
                                    v-model="person.birthdate"
                                    color="primary"
                                    variant="outlined"
                                    density="compact"
                                    data-cy="birthdate"
                                    :disabled="dontAllowEdit"
                                    :label="$t('birthdate')"
                                    :max="$dateFns.format(new Date())"
                                    year-active-picker
                                />
                            </v-col>
                            <v-col v-show="!hideCitizenship" cols="12" sm="4">
                                <v-autocomplete
                                    v-model="person.citizenshipId"
                                    color="primary"
                                    variant="outlined"
                                    density="compact"
                                    data-cy="citizenship-id"
                                    :disabled="dontAllowEdit"
                                    :label="$t('citizenship')"
                                    :items="countries"
                                    item-title="name"
                                    item-value="id"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="col-title" cols="12" md="3">
                        <h4 class="person-form__title text-h6 text--secondary">
                            {{ $t('comment') }}
                        </h4>
                    </v-col>
                    <v-col cols="12" md="9">
                        <div class="px-0 font-italic text-subtitle-2">
                            {{ $t('enter_comment') }}
                        </div>
                        <v-textarea
                            v-model="person.comments"
                            color="primary"
                            variant="outlined"
                            density="compact"
                            data-cy="comments"
                            :disabled="dontAllowEdit"
                        />
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" md="3" class="person-form__body-sidebar order-first order-md-last">
                <v-select
                    v-if="!isRoleDisabled"
                    v-model="person.role"
                    color="primary"
                    variant="outlined"
                    density="compact"
                    data-cy="role"
                    :items="roleItems"
                    :label="`${$t('role')}*`"
                    :rules="[requiredRule('role')]"
                />
                <v-text-field
                    v-else
                    :model-value="$t(`roles.${personRoleWithoutPrefix}`)"
                    color="primary"
                    variant="outlined"
                    density="compact"
                    data-cy="role-without-prefix"
                    disabled
                    :label="`${$t('role')}*`"
                />
                <v-text-field
                    v-model="person.login"
                    color="primary"
                    variant="outlined"
                    density="compact"
                    data-cy="login"
                    :disabled="(!!person.personId && hasLogin) || dontAllowEdit"
                    :label="$t('login')"
                />
                <v-select
                    v-if="showDepartments"
                    v-model="departmentId"
                    color="primary"
                    variant="outlined"
                    density="compact"
                    data-cy="department-id"
                    clearable
                    :disabled="dontAllowEdit || !departments.length"
                    :items="departments"
                    :label="$t('department')"
                    item-title="name"
                    item-value="id"
                />
                <v-select
                    v-model="person.language"
                    color="primary"
                    variant="outlined"
                    density="compact"
                    data-cy="language"
                    clearable
                    :disabled="dontAllowEdit"
                    :label="$t('language')"
                    :items="localesItems"
                    item-title="label"
                    item-value="value"
                />
                <h4 class="person-form__title text-h6 text--secondary py-3">
                    {{ person.personId ? $t('change_password') : $t('password') }}
                </h4>
                <v-text-field
                    v-model="password"
                    color="primary"
                    variant="outlined"
                    density="compact"
                    data-cy="password"
                    :disabled="dontAllowEdit"
                    :label="`${$t('password')}${!credentialsRequired || person.personId ? '' : '*'}`"
                    type="password"
                    autocomplete="new-password"
                    :rules="[
                        v =>
                            !credentialsRequired ||
                            !!v ||
                            !!person.personId ||
                            `${$t('password')} ${$t('validation.required')}`,
                    ]"
                    @update:model-value="passwordToMatch = null"
                />
                <v-text-field
                    v-model="passwordToMatch"
                    color="primary"
                    variant="outlined"
                    density="compact"
                    data-cy="password-confirm"
                    :disabled="dontAllowEdit"
                    :label="`${$t('password_confirm')}${!credentialsRequired || person.personId ? '' : '*'}`"
                    type="password"
                    autocomplete="new-password"
                    :rules="passwordConfirmationRules"
                />
                <h4 class="person-form__title text-h6 text--secondary py-3">
                    {{ $t('avatar') }}
                </h4>
                <v-row>
                    <div class="relative-position px-2 pt-4 pb-10">
                        <v-avatar color="grey" size="150">
                            <v-img
                                v-if="person.avatar || avatar"
                                :src="avatar ? avatar : $filters.imageUrl(person.avatar, 300)"
                                cover
                            />
                        </v-avatar>
                        <div style="position: absolute; bottom: 40px; right: -20px">
                            <v-file-input
                                v-model="filesArray"
                                prepend-icon="mdi-pencil"
                                small-chips
                                variant="outlined"
                                density="compact"
                                accept="image/jpg, image/jpeg, image/png"
                                hide-details
                                :label="$t('select_photo')"
                                data-cy="avatar"
                                class="hide-input"
                                @update:model-value="onPhotoLoad"
                            />
                        </div>
                    </div>
                </v-row>
            </v-col>
        </v-row>
        <v-row no-gutters class="person-form__footer gc-4">
            <v-btn class="btn-cancel" data-cy="cancel" variant="text" :color="'grey-darken-2'" @click="cancel">
                {{ $t('cancel') }}
            </v-btn>
            <v-btn
                :loading="isLoading"
                :disabled="isLoading || dontAllowEdit"
                class="btn-save"
                data-cy="save"
                @click="save"
            >
                {{ $t('save') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script lang="ts">
    import {Component, Emit, Inject, mixins, Prop, Ref, Watch} from 'vue-facing-decorator'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import PhoneField from '@/components/snippets/forms/PhoneField.vue'
    import {appInstance} from '~/utils/app-accessor'
    import {EventBus, SWITCH_LANGUAGE} from '~/utils/event-bus'
    import {blobToBase64, clean, clone} from '~/utils/helpers'
    import type {VForm} from 'vuetify/components'
    import TextFieldLocale from '~/components/TextFieldLocale.vue'
    import {userGetToPutTransformer} from '~/adapters/UpdateUserRQAdapter'

    @Component({
        components: {TextFieldLocale, PhoneField, DatePicker},
    })
    export class PersonForm extends mixins(FormValidationRulesMixin) {
        @Prop() personData
        @Prop() companyId
        @Prop({default: null}) rolePrefix
        @Prop() customRoles
        @Prop({default: false, type: Boolean}) redirect
        @Prop({default: false, type: Boolean}) allowNoCredentials
        @Prop({default: false, type: Boolean}) dontAllowEdit
        @Prop({default: () => [], type: Array}) departments
        @Prop({default: null}) customPersonRoles
        @Inject() goTo

        valid: boolean = null
        isLoading: boolean = false
        password: string = null
        passwordToMatch: string = null
        hasLogin: boolean = false
        departmentId = null
        person = {}
        avatar: string = null
        filesArray = []
        isCorporateUser = false

        activeLangCode: string = 'en'

        get isSupplierCompany(): boolean {
            return runtimeStore.companies[0]?.type === 'INTERNAL_SUPPLIER'
        }

        @Watch('personData')
        async resetPerson() {
            if ('personId' in this.personData) {
                const role = this.personData.role
                if (role && role?.includes('corp.')) {
                    this.activeLangCode = this.locale
                    await this.loadPerson(this.personData.personId)
                    this.isCorporateUser = true
                } else {
                    this.person = clone(this.personData)
                }

                if (this.person?.department?.id) {
                    this.departmentId = this.person.department.id
                }
                if ('active' in this.person) this.person.active = JSON.parse(this.person.active)
                this.hasLogin = !!this.person.login
            }
        }

        beforeUnmount() {
            this.person = {}
        }

        get locale(): string {
            return this.$i18n.locale
        }

        get locales() {
            const availableLocales = Object.values(this.$conf.public.locales)
            return availableLocales.map(lang => {
                return {value: lang, title: this.$t(`languages.${lang}`), icon: useFlagIconCode.value(lang)}
            })
        }

        async loadPerson(id) {
            try {
                this.person = (await this.$api.persons.get({id, locale: 'all'})).users[0]
            } catch (e) {
                this.person = {}
            }
        }

        async mounted() {
            await this.resetPerson()

            runtimeStore.SET_AVAILABLE_PERSON_ROLES([])

            if (this.person.personId) {
                this.person.active = !!this.person.active
            }

            const companyId = this.companyId || this.$route.params.companyId || authStore.person.companyId
            await runtimeStore.loadCompanies({id: companyId, shortResponse: true})
            const companyType = runtimeStore.companies[0]?.type

            if (!this.person.personId) {
                if (companyType === 'CORPORATOR') {
                    this.isCorporateUser = true
                    this.person.firstNameLocalized = {}
                    this.person.middleNameLocalized = {}
                    this.person.lastNameLocalized = {}
                }

                await runtimeStore.loadAvailablePersonRoles(companyType)
            } else {
                await runtimeStore.loadAvailablePersonRoles(companyType, this.person.personId)
            }
        }

        @Ref() form: VForm

        cancel(): void {
            if (this.$route.name === 'home') {
                this.$emit('close-popup')
            } else {
                const companyId = this.person?.companyId === authStore.person.companyId ? null : this.person?.companyId
                const route = companyId
                    ? {name: 'company-client-profile', params: {id: companyId}}
                    : {name: 'companyPersons'}
                this.$router.push(route)
            }
        }

        @Emit()
        async save() {
            const {errors} = await this.form.validate()
            if (!this.valid) {
                this.goTo(document.getElementById(errors[0].id))
                return
            }
            this.isLoading = true
            try {
                const person = userGetToPutTransformer(this.person)
                person.avatar = this.avatar ? this.avatar.split(',')[1] : this.person.avatar
                if (this.departmentId) person.departmentId = this.departmentId
                clean(person)

                let newUser
                if (this.person.personId) {
                    if (this.password) {
                        Object.assign(person, {password: this.password})
                    }
                    newUser = await this.$api.persons.put(this.person.personId, person)
                    if (this.person.personId === authStore.person.personId) {
                        authStore.SET_PERSON({...this.person, ...person})
                    }
                } else {
                    Object.assign(person, {
                        ...(this.password && {password: this.password}),
                        active: true,
                        companyId: this.companyId || this.$route.params.companyId || authStore.person.companyId,
                    })
                    newUser = await this.$api.persons.post(person)
                }
                this.hasLogin = !!this.person.login
                this.$toast.success(this.$t('messages.success.user_saved'))

                if (this.ownAccountEdit && Object.values(appInstance.$conf.public.locales).includes(person.language)) {
                    EventBus.$emit(SWITCH_LANGUAGE, person.language)
                }

                if (this.redirect) {
                    const companyId = this.$route.params.companyId
                    const routName = this.$route.name
                    if (routName === 'supplierPerson') {
                        await this.$router.push({name: 'supplierPerson', params: {id: newUser.personId, companyId}})
                    }
                    const route = companyId
                        ? {name: 'companyPerson', params: {id: newUser.personId, companyId}}
                        : {name: 'companyPersons'}
                    await this.$router.push(route)
                }

                return newUser
            } catch (e) {
                if (e.errors) {
                    e.errors.forEach(error => {
                        if (error.errorType === 'validation') {
                            this.$toast.error(error.message)
                        } else {
                            this.$toast.error(this.$t('error_message.save_error'))
                        }
                    })
                } else {
                    this.$toast.error(this.$t('error_message.save_error'))
                }
            } finally {
                this.isLoading = false
            }
        }

        changeActiveLangCode(code) {
            this.activeLangCode = code
        }

        get countries() {
            return runtimeStore.countries
        }

        get passwordConfirmationRules() {
            return [
                v =>
                    (!this.password && !v) ||
                    this.password === v ||
                    `${this.$t('password_confirm')} ${this.$t('validation.match')}`,
            ]
        }

        get roleItems() {
            return runtimeStore.availablePersonRoles || []
        }

        get credentialsRequired() {
            return !this.allowNoCredentials
        }

        get hideCitizenship() {
            return false
        }

        get hidePassport() {
            return false
        }

        get hideBirthdate() {
            return false
        }

        get hideSkype() {
            return false
        }

        get ownAccountEdit() {
            return authStore.person?.personId !== undefined && authStore.person?.personId === this.person?.personId
        }

        get personRoleWithoutPrefix() {
            return this.person.role ? this.person.role.split('.')[1] || '' : ''
        }

        get localesItems() {
            return Object.values(this.$conf.public.locales)
                .map(el => ({
                    value: el,
                    label: this.$t(`languages_local.${el}`),
                }))
                .sort((a, b) => {
                    return a.label.localeCompare(b.label)
                })
        }

        async onPhotoLoad(): Promise<void> {
            if (this.filesArray) {
                this.avatar = await blobToBase64(this.filesArray)
            } else {
                this.avatar = null
            }
        }

        get isTO1() {
            return authStore.isTO1
        }

        get showDepartments() {
            return runtimeStore.companies[0]?.type !== 'TOUR_AGENCY' && authStore.isTO1
        }

        get isRoleDisabled() {
            if (this.dontAllowEdit || this.ownAccountEdit) {
                return true
            }

            return !this.roleItems?.length
        }
    }

    export default PersonForm
</script>
